import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faChalkboardTeacher,
  faTimes,
  faCheckCircle,
  faPlus,
  faTrash,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import EditTrainerModal from '../../components/admin/EditTrainerModal';

interface InstructorAdmin {
  userId: string;
  instructorRecordId: string;
  employeeId: string;
  fullName: string;
  email: string;
  assignedCourseTitles: string[];
}

interface LearnershipOption {
  id: string;
  title: string;
  isActive: boolean;
}

export default function AdminInstructorsPage() {
  const [instructors, setInstructors] = useState<InstructorAdmin[]>([]);
  const [learnerships, setLearnerships] = useState<LearnershipOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [assignModalInstructor, setAssignModalInstructor] = useState<InstructorAdmin | null>(null);
  const [selectedLearnershipId, setSelectedLearnershipId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);

  const { accessToken } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const [instructorsRes, learnershipsRes] = await Promise.all([
        fetch('/api/admin/instructors', { headers }),
        fetch('/api/admin/learnerships', { headers }),
      ]);

      if (!instructorsRes.ok || !learnershipsRes.ok) throw new Error('Failed to load data');

      setInstructors(await instructorsRes.json());
      setLearnerships(await learnershipsRes.json());
    } catch (err) {
      console.error(err);
      setError('Failed to load trainers. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const openAssignModal = (instructor: InstructorAdmin) => {
    setAssignModalInstructor(instructor);
    setSelectedLearnershipId('');
    setAssignError('');
    setAssignSuccess(false);
  };

  const handleAssign = async () => {
    if (!assignModalInstructor || !selectedLearnershipId) return;

    try {
      setIsAssigning(true);
      setAssignError('');

      const response = await fetch('/api/admin/instructor-assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          instructorUserId: assignModalInstructor.userId,
          learnershipId: selectedLearnershipId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setAssignError(data.message || 'Failed to assign trainer.');
        return;
      }

      setAssignSuccess(true);
      await fetchData();
      setTimeout(() => setAssignModalInstructor(null), 1200);
    } catch (err) {
      console.error(err);
      setAssignError('An error occurred while assigning the trainer.');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemove = async (instructor: InstructorAdmin, learnershipTitle: string) => {
    const learnership = learnerships.find((l) => l.title === learnershipTitle);
    if (!learnership) return;

    try {
      await fetch(`/api/admin/instructor-assignments/${instructor.userId}/${learnership.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const availableLearnerships = learnerships.filter(
    (l) => l.isActive && !assignModalInstructor?.assignedCourseTitles.includes(l.title)
  );

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Trainers/Assessors</h1>
        <p className="text-sm text-brand-900/60 mt-1">Assign trainers to teach and assess courses</p>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && instructors.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faChalkboardTeacher} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No trainer accounts yet</h3>
            <p className="text-sm text-brand-900/60 mt-2">
              Create a trainer account (Admin → Create User) to assign them to courses.
            </p>
          </div>
        )}

        {!isLoading && !error && instructors.length > 0 && (
          <div className="card-surface overflow-hidden">
            <div className="hidden md:grid grid-cols-[2fr_1.5fr_2fr_auto] gap-4 px-5 py-3 bg-canvas-50 text-xs font-bold uppercase tracking-wider text-brand-900/50">
              <span>Trainer</span>
              <span>Employee ID</span>
              <span>Assigned Courses</span>
              <span></span>
            </div>
            <div className="divide-y divide-canvas-200">
              {instructors.map((instructor) => (
                <div
                  key={instructor.userId}
                  className="grid grid-cols-1 md:grid-cols-[2fr_1.5fr_2fr_auto] gap-2 md:gap-4 px-5 py-4 items-center"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-950 truncate">{instructor.fullName}</p>
                    <p className="text-xs text-brand-900/50 truncate">{instructor.email}</p>
                  </div>
                  <p className="text-sm text-brand-900/70">{instructor.employeeId}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {instructor.assignedCourseTitles.length === 0 ? (
                      <span className="text-xs text-brand-900/40">No courses yet</span>
                    ) : (
                      instructor.assignedCourseTitles.map((title) => (
                        <span
                          key={title}
                          className="group px-2 py-0.5 bg-brand-50 text-brand-700 text-xs font-medium rounded-full flex items-center gap-1.5"
                        >
                          {title}
                          <button
                            onClick={() => handleRemove(instructor, title)}
                            className="text-brand-400 hover:text-red-500"
                            title="Remove assignment"
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-2.5 w-2.5" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditUserId(instructor.userId)}
                      className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-1.5 w-fit"
                    >
                      <FontAwesomeIcon icon={faPen} className="h-3 w-3" />
                      Edit
                    </button>
                    <button
                      onClick={() => openAssignModal(instructor)}
                      className="px-3 py-1.5 bg-brand-gradient text-white text-xs font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-1.5 w-fit"
                    >
                      <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                      Assign
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editUserId && <EditTrainerModal userId={editUserId} onClose={() => setEditUserId(null)} onSaved={fetchData} />}

      {assignModalInstructor && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-brand-950">Assign to Course</h3>
              <button onClick={() => setAssignModalInstructor(null)} className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60">
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <p className="text-sm text-brand-900/60 mb-4">
              Assigning <span className="font-semibold text-brand-950">{assignModalInstructor.fullName}</span>
            </p>

            {assignSuccess ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <FontAwesomeIcon icon={faCheckCircle} className="h-5 w-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-700 font-medium">Trainer assigned successfully!</p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Select course</label>
                  <select
                    value={selectedLearnershipId}
                    onChange={(e) => setSelectedLearnershipId(e.target.value)}
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">Choose a course...</option>
                    {availableLearnerships.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.title}
                      </option>
                    ))}
                  </select>
                  {availableLearnerships.length === 0 && (
                    <p className="text-xs text-brand-900/50 mt-2">
                      This trainer is already assigned to all available active courses.
                    </p>
                  )}
                </div>

                {assignError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4 text-red-700 flex-shrink-0" />
                    <p className="text-sm text-red-700">{assignError}</p>
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setAssignModalInstructor(null)}
                    className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssign}
                    disabled={isAssigning || !selectedLearnershipId}
                    className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={isAssigning ? faSpinner : faCheckCircle} className={`h-3.5 w-3.5 ${isAssigning ? 'animate-spin' : ''}`} />
                    {isAssigning ? 'Assigning...' : 'Confirm Assignment'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
